// config/routes.ts - Localized route configuration

export type Language = 'fi' | 'en' | 'sv';

export interface RouteConfig {
  key: string;
  paths: Record<Language, string>;
}

// Define all routes with their localized paths
export const routes: RouteConfig[] = [
  {
    key: 'home',
    paths: { fi: '', en: '', sv: '' }
  },
  {
    key: 'services',
    paths: { fi: 'palvelut', en: 'services', sv: 'tjanster' }
  },
  {
    key: 'pricing',
    paths: { fi: 'hinnasto', en: 'pricing', sv: 'priser' }
  },
  {
    key: 'faq',
    paths: { fi: 'ukk', en: 'faq', sv: 'faq' }
  },
  {
    key: 'shipping',
    paths: { fi: 'toimitus', en: 'shipping', sv: 'leverans' }
  },
  {
    key: 'contact',
    paths: { fi: 'yhteystiedot', en: 'contact', sv: 'kontakt' }
  },
  {
    key: 'privacy',
    paths: { fi: 'tietosuoja', en: 'privacy', sv: 'integritet' }
  },
  {
    key: 'terms',
    paths: { fi: 'ehdot', en: 'terms', sv: 'villkor' }
  }
];

// Get path for a route in a specific language
export const getPath = (key: string, lang: Language): string => {
  const route = routes.find(r => r.key === key);
  if (!route) return '/';
  const path = route.paths[lang];
  return `/${lang}${path ? '/' + path : ''}`;
};

// Get route key from current path
export const getRouteKeyFromPath = (pathname: string): { lang: Language; key: string } | null => {
  // Extract language and path from URL
  const match = pathname.match(/^\/(fi|en|sv)(\/(.*))?$/);
  if (!match) return null;

  const lang = match[1] as Language;
  const path = match[3] || '';

  // Find matching route
  const route = routes.find(r => r.paths[lang] === path);
  if (!route) return null;

  return { lang, key: route.key };
};

// Get equivalent path in another language
export const getEquivalentPath = (currentPath: string, targetLang: Language): string => {
  const current = getRouteKeyFromPath(currentPath);
  if (!current) return `/${targetLang}`;
  return getPath(current.key, targetLang);
};

// Check if path is a valid language path
export const isValidLanguagePath = (pathname: string): boolean => {
  return /^\/(fi|en|sv)(\/|$)/.test(pathname);
};

// Get language from path
export const getLanguageFromPath = (pathname: string): Language | null => {
  const match = pathname.match(/^\/(fi|en|sv)/);
  return match ? match[1] as Language : null;
};
